/**
 * Renders a CMS section by its `type` field.
 * Lightweight, editable sections use inline JSX here.
 * Heavy/legacy sections delegate to existing AGS components.
 */
import { ArrowRight, Mail, Linkedin, ShieldCheck, Leaf } from "lucide-react";
import * as LucideIcons from "lucide-react";
import { useEffect, useState } from "react";
import TrustStrip from "@/components/site/TrustStrip";
import Services from "@/components/site/Services";
import TeamInAction from "@/components/site/TeamInAction";
import SiteLeadership from "@/components/site/SiteLeadership";
import Compliance from "@/components/site/Compliance";
import InHouseExpertise from "@/components/site/InHouseExpertise";
import GreenPledge from "@/components/site/GreenPledge";
import FAQ from "@/components/site/FAQ";
import QuoteForm from "@/components/site/QuoteForm";
import FacilityManagement from "@/components/site/FacilityManagement";
import WhyAGS from "@/components/site/WhyAGS";
import { fetchCollection } from "@/lib/cmsApi";
import useSiteImage from "@/lib/useSiteImage";
import Reveal from "./Reveal";

function Hero({ d }) {
  const { url: img } = useSiteImage(
    "hero_image",
    d.image_url || "https://images.pexels.com/photos/10988763/pexels-photo-10988763.jpeg",
  );
  return (
    <section className="relative min-h-[100vh] flex items-end overflow-hidden bg-ags-cream aaa-noise">
      <div aria-hidden className="absolute inset-0" style={{ backgroundImage: `url(${img})`, backgroundSize: "cover", backgroundPosition: "center" }} />
      <div aria-hidden className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(248,247,245,0) 0%, rgba(248,247,245,.55) 55%, rgba(248,247,245,.98) 100%)" }} />
      <div className="relative container-ags pb-20 md:pb-28 pt-40 w-full">
        {d.eyebrow && (
          <Reveal className="mb-8 flex items-center gap-3" y={12}>
            <span className="h-px w-16 bg-[#2BB6E5]" />
            <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#0E2A57] font-semibold">{d.eyebrow}</span>
          </Reveal>
        )}
        <Reveal delay={0.05} as="h1" className="font-heading font-extrabold text-ags-ink text-5xl md:text-7xl lg:text-[6.5rem] leading-[0.92] tracking-[-0.04em] max-w-5xl">
          {d.title_pre} {d.title_italic && <><br /><span className="italic-display font-normal text-[#0E2A57]">{d.title_italic}</span></>} {d.title_post && <>{" "}{d.title_post}</>}
        </Reveal>
        <Reveal delay={0.15} className="mt-12 grid md:grid-cols-12 gap-10 items-end">
          {d.body && <p className="md:col-span-6 text-ags-text-muted text-lg md:text-2xl leading-[1.55] max-w-xl">{d.body}</p>}
          <div className="md:col-span-6 flex flex-wrap items-center gap-4 md:justify-end">
            {d.primary_cta_label && (
              <a href={d.primary_cta_href || "#"} className="group inline-flex items-center gap-3 bg-[#0E2A57] text-white hover:bg-[#163B75] hover:-translate-y-0.5 px-10 py-5 font-semibold tracking-wide transition-[transform,background-color] duration-300 shadow-ags-ambient hover:shadow-ags-hover">
                {d.primary_cta_label} <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </a>
            )}
            {d.secondary_cta_label && (
              <a href={d.secondary_cta_href || "#"} className="inline-flex items-center gap-3 bg-transparent border border-[#0E2A57] text-[#0E2A57] hover:bg-[#0E2A57] hover:text-white px-10 py-5 font-semibold tracking-wide transition-all">{d.secondary_cta_label}</a>
            )}
          </div>
        </Reveal>
        {Array.isArray(d.kpis) && d.kpis.length > 0 && (
          <Reveal
            delay={0.25}
            y={16}
            className={`mt-12 md:mt-16 pt-6 border-t border-[#0E2A57]/15 grid gap-px bg-[#0E2A57]/15 ${
              d.kpis.length <= 3
                ? "grid-cols-3"
                : d.kpis.length === 4
                ? "grid-cols-2 md:grid-cols-4"
                : "grid-cols-2 md:grid-cols-5"
            }`}
          >
            {d.kpis.map((s, i) => (
              <div
                key={`hero-kpi-${i}`}
                className="bg-ags-cream px-2 md:px-3 py-2.5 md:py-3 flex flex-col transition-colors hover:bg-ags-sand-soft"
              >
                <span className="font-mono text-[8px] uppercase tracking-[0.22em] text-[#0E2A57]/55">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="mt-1 font-heading font-extrabold text-sm md:text-base text-ags-ink tracking-[-0.03em] leading-none">
                  {s.k}
                </span>
                <span className="mt-1 text-[9px] md:text-[10px] text-ags-text-muted leading-snug uppercase tracking-wide">
                  {s.v}
                </span>
              </div>
            ))}
          </Reveal>
        )}
      </div>
    </section>
  );
}

function CustomTrustStrip({ d }) {
  if (!d.items?.length) return <TrustStrip />;
  return (
    <section className="bg-white border-y border-ags-hairline">
      <div className="container-ags py-8 md:py-10">
        <div className="flex flex-col md:flex-row md:items-start gap-6 md:gap-10">
          <div className="md:w-52 flex-shrink-0">
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0E2A57]/55">Accredited &amp; Insured</span>
            <div className="mt-1 font-heading font-bold text-sm text-[#0E2A57] leading-tight">{d.title || "Institutional-grade compliance"}</div>
          </div>
          <div className="flex-1 grid grid-cols-3 md:grid-cols-6 gap-px bg-ags-hairline">
            {d.items.map((b, i) => {
              const Icon = LucideIcons[b.icon] || LucideIcons.ShieldCheck;
              return (
                <div key={`ts-${i}-${b.label}`} className="bg-white px-2 py-3 flex flex-col items-center text-center group hover:bg-ags-cream transition-colors">
                  <Icon size={16} strokeWidth={1.5} className="text-[#0E2A57] group-hover:text-[#2BB6E5] transition-colors duration-500" />
                  <div className="mt-1.5 font-heading font-bold text-[10px] tracking-tight text-[#0E2A57] leading-tight">{b.label}</div>
                  {b.sub && <div className="mt-0.5 font-mono text-[8px] uppercase tracking-[0.15em] text-[#475569]">{b.sub}</div>}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function About({ d }) {
  return (
    <section className="bg-white py-28 md:py-40 border-t border-ags-hairline">
      <div className="container-ags grid md:grid-cols-12 gap-10 md:gap-16">
        <Reveal className="md:col-span-4">
          {d.eyebrow && <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#0E2A57]/60 font-medium">{d.eyebrow}</span>}
          <h2 className="mt-6 font-heading font-extrabold text-4xl md:text-5xl lg:text-6xl leading-[1.0] tracking-[-0.035em] text-ags-ink">
            {d.title_pre} {d.title_italic && <span className="italic-display font-normal text-[#0E2A57]">{d.title_italic}</span>}
          </h2>
        </Reveal>
        <Reveal delay={0.1} className="md:col-span-7 md:col-start-6 space-y-6 text-ags-text-muted text-lg md:text-2xl leading-[1.55]">
          {(d.paragraphs || []).map((p, i) => <p key={i}>{p}</p>)}
        </Reveal>
      </div>
    </section>
  );
}

function AGSMeaning({ d }) {
  const letters = d.letters || [];
  return (
    <section className="bg-[#050B14] text-white py-28 md:py-40 border-t border-[#0E2A57] relative aaa-noise overflow-hidden">
      <div aria-hidden className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: "linear-gradient(to right, rgba(190,231,245,0.6) 1px, transparent 1px), linear-gradient(to bottom, rgba(190,231,245,0.6) 1px, transparent 1px)",
        backgroundSize: "80px 80px",
      }} />
      {/* Sand-cyan radial accent for editorial depth */}
      <div
        aria-hidden
        className="absolute -bottom-40 -right-40 w-[700px] h-[700px] pointer-events-none opacity-[0.06]"
        style={{ background: "radial-gradient(circle, rgba(230,220,200,0.7) 0%, rgba(5,11,20,0) 60%)" }}
      />
      <div className="relative container-ags">
        <div className="max-w-3xl mb-16">
          {d.eyebrow && <div className="flex items-center gap-3 mb-6"><span className="h-px w-16 bg-[#2BB6E5]" /><span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#BEE7F5] font-medium">{d.eyebrow}</span></div>}
          <h2 className="font-heading font-extrabold text-3xl md:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.035em] text-white">{d.title}</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-px bg-white/15 border border-white/20">
          {letters.map((b, i) => (
            <div key={i} className="bg-[#050B14] p-8 md:p-10 hover:bg-[#0E2A57] transition-colors duration-500 group flex flex-col gap-6">
              <div className="flex items-start justify-between">
                <span className="italic-display text-[#BEE7F5] group-hover:text-[#2BB6E5] text-7xl md:text-8xl leading-none font-normal transition-colors duration-500">{b.letter}</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#2BB6E5] mt-3">{String(i+1).padStart(2,"0")} / 03</span>
              </div>
              <div>
                <h3 className="font-heading font-bold text-base md:text-lg text-white tracking-[-0.02em]">{b.title}</h3>
                <p className="mt-3 text-white/70 text-sm md:text-[15px] leading-[1.6]">{b.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FounderQuote({ d }) {
  return (
    <section className="bg-ags-cream py-28 md:py-40 border-t border-ags-hairline aaa-noise">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <Reveal className="bg-white border border-ags-hairline shadow-ags-ambient p-10 md:p-16 text-center relative">
          {/* Decorative editorial quote mark */}
          <span
            aria-hidden
            className="italic-display absolute -top-4 left-8 md:left-12 text-[#0E2A57]/10 text-[8rem] md:text-[10rem] leading-none select-none pointer-events-none"
          >
            &ldquo;
          </span>
          {d.eyebrow && <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0E2A57]/60 font-medium">{d.eyebrow}</span>}
          <blockquote className="mt-6 italic-display text-2xl md:text-[2rem] leading-[1.35] text-ags-ink">{d.quote}</blockquote>
          {d.attribution && <div className="mt-6 font-mono text-[11px] uppercase tracking-[0.22em] text-[#2BB6E5]">{d.attribution}</div>}
        </Reveal>
      </div>
    </section>
  );
}

function CTA({ d }) {
  return (
    <section className="relative bg-[#0E2A57] text-white py-28 md:py-40 overflow-hidden aaa-noise">
      {d.image_url && <div aria-hidden className="absolute inset-0 opacity-30" style={{backgroundImage:`url(${d.image_url})`,backgroundSize:"cover",backgroundPosition:"center"}} />}
      {/* Ambient sand-cyan radial for editorial depth */}
      <div
        aria-hidden
        className="absolute -top-40 left-1/2 -translate-x-1/2 w-[800px] h-[800px] pointer-events-none opacity-[0.12]"
        style={{ background: "radial-gradient(circle, rgba(230,220,200,0.7) 0%, rgba(14,42,87,0) 55%)" }}
      />
      <Reveal className="relative container-ags text-center">
        {d.eyebrow && <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#BEE7F5] mb-6">{d.eyebrow}</div>}
        <h2 className="font-heading font-extrabold text-4xl md:text-6xl leading-[1.02] tracking-[-0.04em] text-white max-w-3xl mx-auto">{d.title}</h2>
        {d.body && <p className="mt-6 text-white/75 text-lg max-w-2xl mx-auto">{d.body}</p>}
        {d.button_label && (
          <a href={d.button_href || "#"} className="group mt-10 inline-flex items-center gap-3 bg-white text-[#0E2A57] hover:bg-ags-sand-soft hover:-translate-y-0.5 px-10 py-5 font-semibold tracking-wide transition-[transform,background-color] duration-300 shadow-ags-ambient hover:shadow-ags-hover">
            {d.button_label} <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
          </a>
        )}
      </Reveal>
    </section>
  );
}

function ImageSection({ d }) {
  if (!d.url) return null;
  return (
    <section className="bg-white py-12 border-t border-ags-hairline">
      <div className="container-ags">
        <img src={d.url} alt={d.caption || ""} className="w-full h-auto" />
        {d.caption && <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em] text-[#475569]">{d.caption}</p>}
      </div>
    </section>
  );
}

function VideoSection({ d }) {
  const src = d.url || d.external_url;
  if (!src) return null;
  if (d.external_url) {
    return <section className="bg-white py-12 border-t border-ags-hairline"><div className="container-ags aspect-video"><iframe src={d.external_url} className="w-full h-full border-0" title="Video" allow="autoplay; encrypted-media; fullscreen" /></div></section>;
  }
  return (
    <section className="bg-white py-12 border-t border-ags-hairline">
      <div className="container-ags">
        <video src={d.url} className="w-full" autoPlay={d.autoplay} loop={d.loop} muted={d.muted || d.autoplay} controls={!d.autoplay} playsInline />
      </div>
    </section>
  );
}

function RichText({ d }) {
  return (
    <section className="bg-white py-16 border-t border-ags-hairline">
      <div className="container-ags prose max-w-3xl" dangerouslySetInnerHTML={{ __html: d.html || "" }} />
    </section>
  );
}

function CustomHTML({ d }) {
  return <div dangerouslySetInnerHTML={{ __html: d.html || "" }} />;
}

function FAQList({ d }) {
  if (!d.items?.length) return <FAQ />;
  return (
    <section className="bg-ags-cream py-28 md:py-40 border-t border-ags-hairline">
      <div className="container-ags grid md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          {d.eyebrow && <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#0E2A57]/60">{d.eyebrow}</span>}
          {d.title && <h2 className="mt-6 font-heading font-extrabold text-4xl tracking-[-0.035em]">{d.title}</h2>}
        </div>
        <div className="md:col-span-8 divide-y divide-ags-hairline border-t border-b border-ags-hairline">
          {d.items.map((it, i) => (
            <details key={i} className="group py-5"><summary className="cursor-pointer font-heading font-bold text-lg flex items-center justify-between">{it.q}<span className="ml-4 text-[#2BB6E5] group-open:rotate-45 transition-transform">+</span></summary><p className="mt-3 text-[#475569] leading-[1.65]">{it.a}</p></details>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProcessSteps({ d }) {
  const steps = d.steps || [];
  return (
    <section className="bg-white py-28 md:py-40 border-t border-ags-hairline">
      <div className="container-ags">
        <div className="grid md:grid-cols-12 gap-10 mb-16">
          <div className="md:col-span-5">
            {d.eyebrow && <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#0E2A57]/60 font-medium">{d.eyebrow}</span>}
            <h2 className="mt-6 font-heading font-extrabold text-4xl md:text-5xl lg:text-6xl leading-[1.0] tracking-[-0.035em] text-ags-ink">
              {d.title_pre} {d.title_italic && <span className="italic-display font-normal text-[#0E2A57]">{d.title_italic}</span>} {d.title_post}
            </h2>
          </div>
          {d.intro && <p className="md:col-span-6 md:col-start-7 text-ags-text-muted text-lg md:text-2xl leading-[1.55] self-end">{d.intro}</p>}
        </div>
        <ol className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {steps.map((s, i) => (
            <li key={i} className="pt-0 group">
              <span className="bg-white border-2 border-[#0E2A57] text-[#0E2A57] w-14 h-14 flex items-center justify-center font-heading font-extrabold text-lg transition-colors group-hover:bg-[#0E2A57] group-hover:text-white">{String(i+1).padStart(2,"0")}</span>
              <h3 className="mt-5 font-heading font-bold text-xl text-ags-ink tracking-[-0.025em]">{s.title}</h3>
              <p className="mt-3 text-ags-text-muted leading-[1.6]">{s.body}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function KPIStrip({ d }) {
  const items = d.items || [];
  return (
    <section className="bg-ags-cream py-12 border-y border-ags-hairline">
      <div className="container-ags grid grid-cols-2 md:grid-cols-4 gap-px bg-[#0E2A57]/15">
        {items.map((it, i) => (
          <div key={`kpi-${i}`} className="bg-ags-cream px-3 md:px-6 py-6">
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0E2A57]/55">{String(i+1).padStart(2,"0")}</span>
            <div className="mt-3 font-heading font-extrabold text-3xl md:text-[2.5rem] text-[#050B14] tracking-[-0.04em] leading-none">{it.k}</div>
            <div className="mt-3 text-xs md:text-sm text-[#475569]">{it.v}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function Testimonials({ d }) {
  const items = d.items || [];
  return (
    <section className="bg-white py-24 border-t border-ags-hairline">
      <div className="container-ags">
        {d.title && <h2 className="font-heading font-extrabold text-4xl md:text-5xl tracking-[-0.035em] text-[#050B14] mb-12">{d.title}</h2>}
        <div className="grid md:grid-cols-3 gap-px bg-[#0E2A57]/15">
          {items.map((it, i) => (
            <div key={i} className="bg-white p-8">
              <blockquote className="italic-display text-xl leading-[1.4] text-[#050B14]">“{it.quote}”</blockquote>
              <div className="mt-6 font-heading font-bold text-[#0E2A57]">{it.author}</div>
              {it.role && <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#475569]">{it.role}</div>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const ServicesGridDynamic = () => <Services />;
const SiteLeadershipDynamic = () => <SiteLeadership />;
const ExpertiseDynamic = () => <InHouseExpertise />;
const QuoteFormDynamic = () => <QuoteForm />;
const FacilityMgmtDynamic = () => <FacilityManagement />;
const WhyAGSDynamic = () => <WhyAGS />;

// ============================================================
//  CMS-driven: Team Gallery, Compliance, Green Pledge
//  Each tries to load its collection; if empty, falls back to the
//  original static component so the site never goes blank.
// ============================================================

function useCollection(name) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetchCollection(name)
      .then((rows) => { if (!cancelled) setData(rows || []); })
      .catch(() => { if (!cancelled) setData([]); });
    return () => { cancelled = true; };
  }, [name]);
  return data;
}

function TeamGalleryDynamic({ d = {} }) {
  const members = useCollection("team");
  if (members === null) return null;
  if (members.length === 0) return <TeamInAction />;
  return (
    <section id="team" data-testid="team-section-dynamic" className="bg-ags-cream py-28 md:py-40 border-t border-ags-hairline">
      <div className="container-ags">
        <div className="max-w-2xl mb-16">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-16 bg-[#2BB6E5]" />
            <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#0E2A57] font-semibold">
              {d.eyebrow || "Our Crew · In Action"}
            </span>
          </div>
          <h2 className="font-heading font-extrabold text-4xl md:text-6xl lg:text-[4.25rem] leading-[0.98] tracking-[-0.038em] text-[#050B14]">
            {d.title || "The people behind the standard."}
          </h2>
          {d.subtitle && <p className="mt-6 text-lg text-[#475569] max-w-xl">{d.subtitle}</p>}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#0E2A57]/15 border border-[#0E2A57]/15">
          {members.map((m) => (
            <article key={m.id} className="bg-white p-6 flex flex-col gap-4">
              {m.photo_url ? (
                <img src={m.photo_url} alt={m.name} className="w-full aspect-[4/5] object-cover" />
              ) : (
                <div className="w-full aspect-[4/5] bg-ags-cream flex items-center justify-center text-[#0E2A57]/20 font-heading font-bold text-7xl">
                  {(m.name || "?")[0]}
                </div>
              )}
              <div>
                <div className="font-heading font-bold text-lg text-[#050B14] tracking-[-0.02em]">{m.name}</div>
                {m.role && <div className="mt-1 text-sm text-[#475569]">{m.role}</div>}
                {m.bio && <p className="mt-3 text-sm text-[#475569] leading-relaxed line-clamp-3">{m.bio}</p>}
                {(m.email || m.linkedin_url) && (
                  <div className="mt-4 flex items-center gap-3 text-[#0E2A57]/60">
                    {m.email && <a href={`mailto:${m.email}`} className="hover:text-[#0E2A57]"><Mail size={14} /></a>}
                    {m.linkedin_url && <a href={m.linkedin_url} target="_blank" rel="noopener noreferrer" className="hover:text-[#0E2A57]"><Linkedin size={14} /></a>}
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function ComplianceDynamic({ d = {} }) {
  const items = useCollection("partners");
  if (items === null) return null;
  const accreditations = items.filter((p) => p.category === "accreditation");
  if (accreditations.length === 0) return <Compliance />;
  return (
    <section data-testid="compliance-section-dynamic" className="bg-white py-28 md:py-40 border-t border-ags-hairline">
      <div className="container-ags">
        <div className="max-w-2xl mb-16">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-16 bg-[#2BB6E5]" />
            <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#0E2A57] font-semibold">
              {d.eyebrow || "Accreditations · Compliance"}
            </span>
          </div>
          <h2 className="font-heading font-extrabold text-4xl md:text-6xl lg:text-[4.25rem] leading-[0.98] tracking-[-0.038em] text-[#050B14]">
            {d.title || "Institutional-grade compliance."}
          </h2>
          {d.subtitle && <p className="mt-6 text-lg text-[#475569] max-w-xl">{d.subtitle}</p>}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-px bg-[#0E2A57]/15 border border-[#0E2A57]/15">
          {accreditations.map((p) => (
            <div key={p.id} className="bg-white p-6 flex flex-col items-center justify-center text-center min-h-[160px] hover:bg-ags-cream transition-colors">
              {p.logo_url ? (
                <img src={p.logo_url} alt={p.name} className="max-h-16 max-w-full object-contain mb-3" />
              ) : (
                <ShieldCheck size={28} className="text-[#0E2A57]/40 mb-3" />
              )}
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0E2A57] font-semibold">{p.name}</div>
              {p.summary && (
                <div className="mt-1.5 font-mono text-[9px] tracking-[0.18em] text-[#475569] uppercase">
                  {p.summary}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function GreenPledgeDynamic({ d = {} }) {
  // If the section has explicit CMS-edited fields (title/body/bullets/image), render them.
  // Otherwise fall back to the static GreenPledge component.
  const hasCmsContent = d && (d.title || d.body || (Array.isArray(d.bullets) && d.bullets.length > 0));
  if (!hasCmsContent) return <GreenPledge />;
  return (
    <section data-testid="green-pledge-dynamic" className="bg-[#E8F5E9] py-28 md:py-40 border-t border-[#C8E6C9]">
      <div className="container-ags grid md:grid-cols-12 gap-12 items-center">
        <div className="md:col-span-7">
          <div className="flex items-center gap-3 mb-6">
            <Leaf size={16} className="text-[#2E7D32]" />
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#2E7D32] font-medium">
              {d.eyebrow || "Our Green Pledge"}
            </span>
          </div>
          <h2 className="font-heading font-extrabold text-4xl md:text-6xl lg:text-[4.25rem] leading-[0.98] tracking-[-0.038em] text-[#050B14]">
            {d.title}
          </h2>
          {d.body && <p className="mt-6 text-lg text-[#365942] max-w-xl leading-relaxed">{d.body}</p>}
          {Array.isArray(d.bullets) && d.bullets.length > 0 && (
            <ul className="mt-8 space-y-3">
              {d.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3 text-[#365942]">
                  <Leaf size={14} className="mt-1.5 text-[#2E7D32] flex-shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        {d.image_url && (
          <div className="md:col-span-5">
            <img src={d.image_url} alt="" className="w-full aspect-[4/5] object-cover border border-[#C8E6C9]" />
          </div>
        )}
      </div>
    </section>
  );
}

/* =========================================================================
 * NEW AAA B2B TRUST SECTIONS
 * ========================================================================= */

function NumbersBlock({ d = {} }) {
  const stats = d.stats && d.stats.length ? d.stats : [
    { k: "500+",  v: "Sites Under Contract" },
    { k: "€12.5M", v: "Public Liability Cover" },
    { k: "24h",   v: "Quotation Turnaround" },
    { k: "100%",  v: "SafePass · CSCS Trained" },
    { k: "12 yr", v: "Family Ownership" },
    { k: "32/32", v: "Counties Reachable" },
  ];
  return (
    <section className="bg-white py-28 md:py-40 border-t border-ags-hairline">
      <div className="container-ags">
        <Reveal className="max-w-2xl mb-14">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-16 bg-[#2BB6E5]" />
            <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#0E2A57] font-semibold">
              {d.eyebrow || "By the Numbers"}
            </span>
          </div>
          <h2 className="font-heading font-extrabold text-4xl md:text-6xl lg:text-[4.25rem] leading-[0.98] tracking-[-0.038em] text-ags-ink">
            {d.title_pre || "Institutional scale."}{" "}
            <span className="italic-display font-normal text-[#0E2A57]">
              {d.title_italic || "Family precision."}
            </span>
          </h2>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-ags-hairline-strong border border-ags-hairline-strong">
          {stats.map((s, i) => (
            <Reveal
              key={`num-${i}`}
              delay={Math.min(i * 0.05, 0.35)}
              y={16}
              className="bg-white px-5 py-7 md:py-8 flex flex-col hover:bg-ags-sand-soft transition-colors"
            >
              <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#0E2A57]/55">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="mt-3 font-heading font-extrabold text-3xl md:text-[2.5rem] text-ags-ink tracking-[-0.035em] leading-none tabular-nums">
                {s.k}
              </span>
              <span className="mt-3 text-xs md:text-sm text-ags-text-muted leading-snug">{s.v}</span>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ClientLogos({ d = {} }) {
  const clients = d.clients && d.clients.length ? d.clients : [
    "An Post", "Musgrave Group", "Bord na Móna", "LIDL Ireland",
    "Dublin Airport", "SuperValu", "Beacon Hospital", "Bausch & Lomb",
    "Kerry Group", "Sisk Group", "Roadstone", "Uniphar Health",
  ];
  return (
    <section className="bg-ags-cream py-28 md:py-40 border-t border-ags-hairline aaa-noise">
      <div className="container-ags">
        <Reveal className="mb-16 md:mb-20 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-16 bg-[#2BB6E5]" />
              <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#0E2A57] font-semibold">
                {d.eyebrow || "Trusted by Ireland's Best"}
              </span>
            </div>
            <h2 className="font-heading font-extrabold text-3xl md:text-5xl leading-[1.02] tracking-[-0.035em] text-ags-ink">
              {d.title || "The names on our contracts."}
            </h2>
          </div>
          {d.subtitle && (
            <p className="text-ags-text-muted text-base leading-relaxed max-w-sm">{d.subtitle}</p>
          )}
        </Reveal>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-px bg-ags-hairline-strong border border-ags-hairline-strong">
          {clients.map((c, i) => (
            <Reveal
              key={`cli-${i}`}
              delay={Math.min(i * 0.04, 0.4)}
              y={12}
              className="bg-white aspect-[3/2] flex items-center justify-center px-6 py-6 group hover:bg-ags-sand-soft transition-colors"
            >
              {typeof c === "string" ? (
                <div className="text-center">
                  <div className="font-heading font-bold text-base md:text-lg text-[#0E2A57] tracking-[-0.02em] leading-tight opacity-60 group-hover:opacity-100 transition-opacity">
                    {c}
                  </div>
                </div>
              ) : (
                <img
                  src={c.logo_url}
                  alt={c.name}
                  className="max-h-10 max-w-full object-contain grayscale opacity-70 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-500"
                />
              )}
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.15} className="mt-10 flex flex-wrap items-center gap-6 justify-between font-mono text-[11px] uppercase tracking-[0.22em] text-[#0E2A57]/60">
          <span>Selected clients · 500+ sites across contract portfolio</span>
          <a href="/services" className="link-arrow text-[#0E2A57] hover:text-[#2BB6E5] transition-colors">
            View services →
          </a>
        </Reveal>
      </div>
    </section>
  );
}

function CaseStudies({ d = {} }) {
  const studies = d.studies && d.studies.length ? d.studies : [
    {
      sector: "Distribution · Logistics",
      image: "/services/post-construction.jpg",
      client: "Tier-1 Distribution Group",
      title: "12,000 m² handover in a 48-hour window.",
      body: "New-build regional distribution centre required Builders + Sparkle Clean phases before Practical Completion. Zero programme slippage.",
      kpis: [
        { k: "48 h",   v: "Turnaround" },
        { k: "12k m²", v: "Site footprint" },
        { k: "0",      v: "Snag-list defects" },
      ],
    },
    {
      sector: "Healthcare · EVS",
      client: "Regional Acute Hospital",
      title: "Terminal-clean protocol for 320-bed acute setting.",
      body: "Full-cycle EVS transition programme aligned to HIQA and HSE infection-control standards. Discharge cleans measured against 45-min SLA.",
      kpis: [
        { k: "45 min", v: "Discharge SLA" },
        { k: "320",   v: "Beds served" },
        { k: "24/7",  v: "Coverage" },
      ],
    },
    {
      sector: "Retail · Multi-Site",
      client: "National Grocery Group",
      title: "Nightly clean across 42 retail units, one framework.",
      body: "Consolidated cleaning contract replacing seven regional suppliers. Single point of contact, unified RAMS, monthly manager audits at every store.",
      kpis: [
        { k: "42",   v: "Stores nightly" },
        { k: "1",    v: "Framework contract" },
        { k: "-18%", v: "Total cost vs prior" },
      ],
    },
  ];
  return (
    <section id="case-studies" className="bg-white py-28 md:py-40 border-t border-ags-hairline">
      <div className="container-ags">
        <Reveal className="mb-16 md:mb-24 flex flex-col md:flex-row md:items-end md:justify-between gap-10">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-16 bg-[#2BB6E5]" />
              <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#0E2A57] font-semibold">
                {d.eyebrow || "Selected Case Studies"}
              </span>
            </div>
            <h2 className="font-heading font-extrabold text-4xl md:text-6xl lg:text-[4.25rem] leading-[0.98] tracking-[-0.038em] text-ags-ink">
              {d.title_pre || "Contracts that stand up"}{" "}
              <span className="italic-display font-normal text-[#0E2A57]">
                {d.title_italic || "to scrutiny."}
              </span>
            </h2>
          </div>
          {d.subtitle && (
            <p className="md:max-w-sm text-ags-text-muted text-base leading-relaxed">{d.subtitle}</p>
          )}
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {studies.map((s, i) => (
            <Reveal
              key={`cs-${i}`}
              delay={Math.min(i * 0.08, 0.4)}
              y={20}
            >
              <article className="group bg-white border border-ags-hairline shadow-ags-ambient hover:shadow-ags-hover hover:-translate-y-1 hover:border-ags-hairline-strong transition-[transform,box-shadow,border-color] duration-500 h-full flex flex-col">
                <div className="aspect-[16/10] overflow-hidden bg-ags-cream relative">
                  {s.image ? (
                    <img
                      src={s.image}
                      alt={s.title}
                      loading="lazy"
                      className="w-full h-full object-cover img-mono group-hover:scale-[1.02] transition-transform duration-[900ms]"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#0E2A57]/10 font-heading font-bold text-9xl italic-display">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                  )}
                  <span className="absolute top-3 left-3 font-mono text-[10px] tracking-[0.22em] uppercase text-white bg-[#0E2A57]/85 backdrop-blur-sm px-2 py-1">
                    {String(i + 1).padStart(2, "0")} · {s.sector}
                  </span>
                </div>
                <div className="p-6 md:p-8 flex-1 flex flex-col">
                  {s.client && (
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#2BB6E5] font-semibold">
                      {s.client}
                    </div>
                  )}
                  <h3 className="mt-3 font-heading font-bold text-xl md:text-2xl text-ags-ink tracking-[-0.025em] leading-[1.15]">
                    {s.title}
                  </h3>
                  {s.body && (
                    <p className="mt-4 text-sm text-ags-text-muted leading-relaxed">{s.body}</p>
                  )}
                  {s.kpis && s.kpis.length > 0 && (
                    <div className="mt-6 pt-6 border-t border-ags-hairline grid grid-cols-3 gap-3">
                      {s.kpis.map((k, j) => (
                        <div key={j}>
                          <div className="font-heading font-extrabold text-lg md:text-xl text-[#0E2A57] tracking-[-0.02em] tabular-nums">{k.k}</div>
                          <div className="mt-1 font-mono text-[8px] uppercase tracking-[0.2em] text-ags-text-muted leading-tight">{k.v}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </article>
            </Reveal>
          ))}
        </div>
        {d.footnote !== false && (
          <Reveal delay={0.2} className="mt-10 flex items-center justify-between flex-wrap gap-4 font-mono text-[11px] uppercase tracking-[0.22em] text-[#0E2A57]/60">
            <span>Client names redacted per NDA · full references available on tender request</span>
            <a href="#quote" className="link-arrow text-[#0E2A57] hover:text-[#2BB6E5] transition-colors">
              Request references →
            </a>
          </Reveal>
        )}
      </div>
    </section>
  );
}

function CoverageMap({ d = {} }) {
  const cities = d.cities || [
    { name: "Dublin",    x: 78, y: 45, tier: 1 },
    { name: "Cork",      x: 52, y: 82, tier: 1 },
    { name: "Galway",    x: 34, y: 42, tier: 1 },
    { name: "Limerick",  x: 43, y: 60, tier: 1 },
    { name: "Waterford", x: 68, y: 72, tier: 2 },
    { name: "Belfast",   x: 74, y: 20, tier: 2 },
    { name: "Sligo",     x: 45, y: 22, tier: 2 },
    { name: "Kilkenny",  x: 65, y: 63, tier: 3 },
    { name: "Athlone",   x: 55, y: 44, tier: 3 },
  ];
  return (
    <section className="bg-[#050B14] text-white py-28 md:py-40 border-t border-[#0E2A57] relative aaa-noise overflow-hidden">
      <div aria-hidden className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: "linear-gradient(to right, rgba(190,231,245,0.6) 1px, transparent 1px), linear-gradient(to bottom, rgba(190,231,245,0.6) 1px, transparent 1px)",
        backgroundSize: "80px 80px",
      }} />
      <div className="container-ags grid md:grid-cols-12 gap-12 md:gap-16 items-start relative">
        <Reveal className="md:col-span-5">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-16 bg-[#2BB6E5]" />
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#2BB6E5] font-medium">
              {d.eyebrow || "Nationwide Coverage"}
            </span>
          </div>
          <h2 className="font-heading font-extrabold text-4xl md:text-6xl lg:text-[4.25rem] leading-[0.98] tracking-[-0.038em] text-white">
            {d.title_pre || "One island."}{" "}
            <span className="italic-display font-normal text-[#BEE7F5]">
              {d.title_italic || "One standard."}
            </span>
          </h2>
          <p className="mt-6 text-lg text-white/70 leading-relaxed max-w-md">
            {d.body || "From Malin Head to Mizen, our regional supervisors mobilise crews within 24 hours. Same blue vest. Same standard. Every county."}
          </p>
          <div className="mt-10 grid grid-cols-2 gap-6 max-w-md">
            <div>
              <div className="font-heading font-extrabold text-3xl md:text-4xl text-white tabular-nums">32/32</div>
              <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.22em] text-white/50">Counties Reachable</div>
            </div>
            <div>
              <div className="font-heading font-extrabold text-3xl md:text-4xl text-white tabular-nums">24 h</div>
              <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.22em] text-white/50">Mobilisation SLA</div>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.15} className="md:col-span-7 relative">
          <div className="relative aspect-[3/4] max-w-md mx-auto">
            <svg viewBox="0 0 100 130" className="absolute inset-0 w-full h-full" aria-hidden>
              <path
                d="M 45 8 Q 55 6, 68 10 Q 78 15, 82 25 Q 86 32, 84 42 Q 82 52, 78 58 Q 82 68, 78 78 Q 72 88, 62 96 Q 50 104, 40 100 Q 28 96, 22 86 Q 18 78, 20 68 Q 14 58, 16 46 Q 14 34, 22 26 Q 30 18, 38 14 Q 42 10, 45 8 Z"
                fill="rgba(43, 182, 229, 0.05)"
                stroke="rgba(43, 182, 229, 0.35)"
                strokeWidth="0.4"
              />
              {cities.map((c, i) => {
                const size = c.tier === 1 ? 1.6 : c.tier === 2 ? 1.1 : 0.7;
                return (
                  <g key={c.name}>
                    <circle cx={c.x} cy={c.y} r={size + 0.8} fill="rgba(43, 182, 229, 0.15)" />
                    <circle cx={c.x} cy={c.y} r={size} fill="#2BB6E5" />
                    <text
                      x={c.x + 3}
                      y={c.y + 1}
                      fontSize="2.4"
                      fill={c.tier === 1 ? "#FFFFFF" : "rgba(255,255,255,0.55)"}
                      fontFamily="'JetBrains Mono', monospace"
                      style={{ letterSpacing: "0.14em", textTransform: "uppercase" }}
                    >
                      {c.name}
                    </text>
                  </g>
                );
              })}
            </svg>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function OnboardingTimeline({ d = {} }) {
  const steps = d.steps || [
    { day: "Day 1",  title: "Site Survey",     body: "Contract manager visits, walks the premises, documents every requirement in a scoped brief." },
    { day: "Day 3",  title: "Written Quote",   body: "Transparent line-item proposal delivered — labour, chemicals, machinery, and contingency all disclosed." },
    { day: "Day 7",  title: "RAMS Sign-Off",   body: "Method Statements and Risk Assessments approved by your H&S manager before any operative steps on site." },
    { day: "Day 14", title: "Team Induction",  body: "Dedicated crew inducted to site protocols, PPE, evacuation routes, key-holder procedures." },
    { day: "Day 30", title: "First Manager Audit", body: "Contract Manager returns for a full quality audit, confirms KPI baseline, agrees continuous improvement plan." },
  ];
  return (
    <section id="onboarding" className="bg-ags-cream py-28 md:py-40 border-t border-ags-hairline">
      <div className="container-ags">
        <Reveal className="mb-16 max-w-3xl">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-16 bg-[#2BB6E5]" />
            <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#0E2A57] font-semibold">
              {d.eyebrow || "How Onboarding Works"}
            </span>
          </div>
          <h2 className="font-heading font-extrabold text-4xl md:text-6xl lg:text-[4.25rem] leading-[0.98] tracking-[-0.038em] text-ags-ink">
            {d.title_pre || "Signed contract"}{" "}
            <span className="italic-display font-normal text-[#0E2A57]">
              {d.title_italic || "to first audit —"}
            </span>{" "}
            {d.title_post || "30 days flat."}
          </h2>
          {d.subtitle && (
            <p className="mt-6 text-ags-text-muted text-lg leading-relaxed">{d.subtitle}</p>
          )}
        </Reveal>
        <ol className="relative grid grid-cols-1 md:grid-cols-5 gap-px bg-ags-hairline-strong border border-ags-hairline-strong">
          {steps.map((s, i) => (
            <Reveal
              key={`ob-${i}`}
              delay={Math.min(i * 0.08, 0.4)}
              y={16}
              as="li"
              className="bg-white p-6 md:p-8 flex flex-col min-h-[240px] group hover:bg-ags-sand-soft transition-colors"
            >
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#2BB6E5] font-semibold">{s.day}</span>
              <span className="mt-2 font-heading font-extrabold text-3xl md:text-4xl text-[#0E2A57] tracking-[-0.035em] leading-none tabular-nums">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h3 className="mt-6 font-heading font-bold text-lg text-ags-ink tracking-[-0.02em]">{s.title}</h3>
              <p className="mt-3 text-sm text-ags-text-muted leading-relaxed flex-1">{s.body}</p>
            </Reveal>
          ))}
        </ol>
      </div>
    </section>
  );
}

const RENDERERS = {
  hero: Hero,
  trust_strip: CustomTrustStrip,
  about: About,
  ags_meaning: AGSMeaning,
  founder_quote: FounderQuote,
  services_grid: ServicesGridDynamic,
  team_gallery: TeamGalleryDynamic,
  process_steps: ProcessSteps,
  site_leadership: SiteLeadershipDynamic,
  compliance: ComplianceDynamic,
  expertise: ExpertiseDynamic,
  green_pledge: GreenPledgeDynamic,
  faq: FAQList,
  kpi_strip: KPIStrip,
  cta: CTA,
  testimonials: Testimonials,
  image: ImageSection,
  video: VideoSection,
  rich_text: RichText,
  custom_html: CustomHTML,
  quote_form: QuoteFormDynamic,
  facility_management: FacilityMgmtDynamic,
  why_ags: WhyAGSDynamic,
  numbers_block: NumbersBlock,
  client_logos: ClientLogos,
  case_studies: CaseStudies,
  coverage_map: CoverageMap,
  onboarding_timeline: OnboardingTimeline,
};

// Section type → in-page anchor id (kept in sync with Navigation.jsx ABOUT_DROPDOWN hashes)
const SECTION_ANCHORS = {
  about: "about",
  ags_meaning: "meaning",
  founder_quote: "guarantee",
  services_grid: "services",
  site_leadership: "site-leadership",
  team_gallery: "team",
  why_ags: "why-ags",
  process_steps: "process",
  compliance: "compliance",
  expertise: "expertise",
  green_pledge: "green-pledge",
  faq: "faq",
  quote_form: "quote",
  facility_management: "facility-management",
  numbers_block: "numbers",
  client_logos: "clients",
  case_studies: "case-studies",
  coverage_map: "coverage",
  onboarding_timeline: "onboarding",
};

export default function SectionRenderer({ section }) {
  if (!section || section.enabled === false) return null;
  const Comp = RENDERERS[section.type];
  if (!Comp) return null;
  const anchor = SECTION_ANCHORS[section.type];
  const inner = <Comp d={section.data || {}} />;
  // Wrap the rendered section in an offset-scroll anchor div so in-page nav
  // (e.g. /#about, /#compliance) scrolls reliably even when the inner
  // component already has its own id (duplicate ids are harmless here and
  // the closest-ancestor wins for in-page scrolling).
  if (!anchor) return inner;
  return <div id={anchor} data-section-type={section.type} className="scroll-mt-24">{inner}</div>;
}
